import { NgClass } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    input,
    output,
    signal,
    ViewEncapsulation,
} from '@angular/core';

/**
 * Avatar do usuário/entidade.
 *
 * Exibe uma imagem (via URL em {@link image} ou conteúdo em {@link base64}) ou,
 * na ausência de imagem, as iniciais informadas em {@link letters}. É renderizado
 * como um `<button>` e emite {@link click} ao ser acionado.
 *
 * Estilos encapsulados (`ViewEncapsulation.Emulated`); usa apenas tokens do
 * design system.
 */
@Component({
    selector: 'ai-avatar',
    standalone: true,
    templateUrl: './avatar.component.html',
    styleUrls: ['./avatar.component.scss'],
    imports: [NgClass],
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Avatar implements AfterViewInit {
    /** Formato da imagem fornecida em {@link base64}. */
    readonly imageFormat = input<'png' | 'jpeg'>('png');

    /** Conteúdo da imagem em base64 (sem o prefixo `data:`). */
    readonly base64 = input<string | null | undefined>(null);

    /** URL da imagem a ser exibida. Tem precedência sobre {@link letters}. */
    readonly image = input<string | null | undefined>(null);

    /** Iniciais exibidas quando não há imagem. */
    readonly letters = input<string | undefined>('SP');

    /** Formato do avatar (reservado para uso futuro; hoje sempre arredondado). */
    readonly shape = input<'box' | 'round'>('round');

    /** Emite o evento de clique no avatar. */
    readonly click = output<any | null>();

    /** Largura/altura do avatar (qualquer unidade CSS). */
    readonly size = input<string | null>('44px');

    /** Tamanho da fonte das iniciais (qualquer unidade CSS). */
    readonly fontSize = input<string | null>(null);

    /** Valor de `background-image` calculado a partir de {@link image}/{@link base64}. */
    protected readonly backgroundImage = signal<string>('');

    ngAfterViewInit(): void {
        if (this.image()) {
            this.backgroundImage.set(`url("${this.image()}")`);
        } else if (this.base64()) {
            this.backgroundImage.set(
                `url("data:image/${this.imageFormat()};base64,${this.base64()}`,
            );
        }
    }

    /**
     * Trata o clique no avatar, repassando o evento para o output {@link click}
     * e impedindo a propagação para elementos ancestrais.
     */
    onClick(event: any): void {
        this.click.emit(event);
        event.stopPropagation();
    }
}
